import { environment } from '@env/environment';
import { LoginContextService } from '@shared/lib/services/login-context.service';
import { SharedLoginConstants } from '@shared/lib/shared-login.constants';
import { WINDOW_TOKEN } from '@usana/ux/common';
import { UsanaMarketService } from '@usana/ux/markets';
import { TranslationService } from '@usana/ux/translations';
import { UxDateLocaleService } from '@usana/ux/universal-components/input/datepicker';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Component, Inject, OnDestroy } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'page-header',
  templateUrl: 'page-header.component.html',
  styleUrls: ['page-header.component.scss'],
  standalone: false,
})
export class PageHeaderComponent implements OnDestroy {
  title: string;
  description: string;
  disableWidthLimit: boolean;
  coverPage: boolean;
  pageClass: string;
  noCancelSection: boolean;

  private subscriptions = new Subscription();

  constructor(
    private readonly usanaTranslationService: TranslationService,
    private readonly router: Router,
    private readonly metaService: Meta,
    private readonly titleService: Title,
    private readonly activatedRoute: ActivatedRoute,
    private readonly loginContextService: LoginContextService,
    private readonly usanaMarketService: UsanaMarketService,
    private readonly uxDateLocaleService: UxDateLocaleService,
    @Inject(WINDOW_TOKEN) private readonly window: any,
  ) {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map(() => ({
            hideTitleTag: this.recursivelyLookForData(this.activatedRoute, 'hideTitleTag', false),
            titleTag: this.recursivelyLookForData(this.activatedRoute, 'titleTag', environment.app.defaultTitle),
            disableWidthLimit: this.recursivelyLookForData(this.activatedRoute, 'disableWidthLimit', false),
            coverPage: this.recursivelyLookForData(this.activatedRoute, 'coverPage', false),
            pageClass: this.recursivelyLookForData(this.activatedRoute, 'pageClass', ''),
            noCancelSection: this.recursivelyLookForData(this.activatedRoute, 'noCancelSection', false),
            descTag: this.recursivelyLookForData(this.activatedRoute, 'descTag', environment.app.defaultDescription),
            hideDescTag: this.recursivelyLookForData(this.activatedRoute, 'hideDescTag', false),
          })),
        )
        .subscribe((pageInfo: any) => {
          this.disableWidthLimit = pageInfo.disableWidthLimit;
          this.coverPage = pageInfo.coverPage;
          this.pageClass = pageInfo.pageClass;
          this.noCancelSection = pageInfo.noCancelSection;
          this.usanaTranslationService
            .translateMultiAsPromise(SharedLoginConstants.TRANSLATION_PKG, [pageInfo.titleTag, pageInfo.descTag])
            .then((translations) => {
              const title = translations[pageInfo.titleTag];
              this.title = pageInfo.hideTitleTag ? '' : title;
              this.description = pageInfo.hideDescription ? '' : translations[pageInfo.descTag];
              this.titleService.setTitle(title);
              this.metaService.updateTag({ property: 'og:title', content: title });
              this.metaService.updateTag({ property: 'og:description', content: this.description });
              this.loadOneTrustForLocale();
            });
        }),
    );

    const i18NInfoSubject = this.usanaMarketService.geti18NInfoSubject();
    if (i18NInfoSubject) {
      this.subscriptions.add(
        i18NInfoSubject.subscribe((i18nInfo) => {
          const usanaLocaleToI18NLocale = {
            kr: 'ko',
            in: 'id',
            jp: 'ja',
            ms: 'en',
          };
          const langCode = i18nInfo.current.phxMarketId.split('-')[0];
          const locale = usanaLocaleToI18NLocale[langCode] || langCode;
          this.uxDateLocaleService.setLocale(locale);
          // Drives the :lang() font overrides in styles.scss: Nanum for ko, NotoSans
          // for zh/ja/th, and Centra No.2 (the default) for everything else.
          document.documentElement.lang = locale;
        }),
      );
    }
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
      this.subscriptions = new Subscription();
    }
  }

  loadOneTrustForLocale() {
    // OneTrust update banner locale switch
    if (this.window.OneTrust) {
      const ssoMarket = this.usanaMarketService.unsafeGetI18nInfo().current.phxMarketId.toLowerCase();
      this.window.OneTrust.changeLanguage(ssoMarket);
      // wait a second after we changeLanguage to see if it returned a translation
      setTimeout(() => {
        const oneTrustLocales = this.getOneTrustLocales();
        if (!oneTrustLocales.includes(ssoMarket)) {
          // didn't find a language file so fetch the default and end
          this.window.OneTrust.changeLanguage(ssoMarket.split('-')[0]);
        }
      }, 1000);
    }
  }

  showCancel(): boolean {
    return !environment.app.isBaoying && this.loginContextService.hasRedirectUri();
  }

  cancel(event): void {
    event.preventDefault();
    // customer type doesn't matter in this context because we show this button
    // only when we have an explicit redirectUri
    this.loginContextService.navigateToRedirectUri(undefined);
  }

  getOneTrustLocales(): string[] {
    const networkCapture: PerformanceEntry[] = this.window.performance
      .getEntriesByType('resource')
      .filter((r: PerformanceResourceTiming) => r.initiatorType === 'fetch');
    const regEx = /.*(consent).*(?<returnedLocale>.{4}\S)\.json/gi;
    return networkCapture
      .filter((x) => x.name.match(regEx))
      .map(
        (a) =>
          a.name
            .split('/')
            .filter((b) => b.includes('.json'))[0]
            .split('.')[0],
      );
  }

  private recursivelyLookForData(route: ActivatedRoute, dataAttribute: string, defaultValue: any): any {
    if (route === null) {
      return defaultValue;
    }
    if (route.snapshot.data[dataAttribute]) {
      return route.snapshot.data[dataAttribute];
    } else {
      // recurse
      return this.recursivelyLookForData(route.firstChild, dataAttribute, defaultValue);
    }
  }
}
